// Ported from ~/Documents/KEP/website/src/lib/truchet.ts — pure functions, no DOM.

// ---- Physical geometry, all in mm (1 SVG user unit = 1mm) ----
export const GRID_N = 4;
export const TILE_SIZE_MM = 35;
export const INSET_MM = 5;
export const BORDER_MM = 4.2;
export const OUTER_MARGIN_MM = 20;

export const GRID_SIZE_MM = TILE_SIZE_MM * GRID_N;
export const INNER_CLEAR_MM = GRID_SIZE_MM + 2 * INSET_MM;
export const BORDER_PATH_SIZE_MM = INNER_CLEAR_MM + BORDER_MM;
export const BORDER_OUTER_MM = BORDER_PATH_SIZE_MM + BORDER_MM;
export const CANVAS_SIZE_MM = GRID_SIZE_MM + 2 * (INSET_MM + BORDER_MM + OUTER_MARGIN_MM);

export const GRID_ORIGIN_MM = (CANVAS_SIZE_MM - GRID_SIZE_MM) / 2;
export const BORDER_ORIGIN_MM = (CANVAS_SIZE_MM - BORDER_PATH_SIZE_MM) / 2;

const STATE_MAP = {
    A: ['tile1', 0],
    B: ['tile1', 90],
    C: ['tile1', 180],
    D: ['tile1', 270],
    '0': ['tile2', 0],
    '1': ['tile2', 90],
};

const VALID_CHARS = 'ABCD01';

export function normalizeState(s) {
    return s.toUpperCase();
}

/** True only for a complete, exactly-16-character valid state string. */
export function isValidState(s) {
    if (s.length !== GRID_N * GRID_N) return false;
    const normalized = normalizeState(s);
    for (const ch of normalized) {
        if (!VALID_CHARS.includes(ch)) return false;
    }
    return true;
}

/**
 * Filters arbitrary input down to a valid (possibly partial) state string:
 * uppercases, drops any character not in the valid alphabet, and caps the
 * result at 16 characters. Safe to call on every keystroke or paste event.
 */
export function sanitizeState(raw) {
    const upper = normalizeState(raw);
    let out = '';
    for (const ch of upper) {
        if (VALID_CHARS.includes(ch)) {
            out += ch;
            if (out.length === GRID_N * GRID_N) break;
        }
    }
    return out;
}

export function randomState() {
    let out = '';
    for (let i = 0; i < GRID_N * GRID_N; i++) {
        out += VALID_CHARS[Math.floor(Math.random() * VALID_CHARS.length)];
    }
    return out;
}

/**
 * Builds the full canvas SVG for a state string of any length from 0 to 16.
 * Grid positions beyond the string's length are left blank (no tile drawn).
 * Throws if the string is longer than 16 or contains an invalid character.
 */
export function buildSvg(state, tiles) {
    const normalized = normalizeState(state);
    if (normalized.length > GRID_N * GRID_N) {
        throw new Error(`State string too long: ${state}`);
    }
    for (const ch of normalized) {
        if (!VALID_CHARS.includes(ch)) {
            throw new Error(`Invalid character in state string: ${ch}`);
        }
    }

    const parts = [];
    parts.push(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CANVAS_SIZE_MM} ${CANVAS_SIZE_MM}" width="${CANVAS_SIZE_MM}mm" height="${CANVAS_SIZE_MM}mm">`
    );
    parts.push(`<title>${normalized}</title>`);
    parts.push(`<rect x="0" y="0" width="${CANVAS_SIZE_MM}" height="${CANVAS_SIZE_MM}" fill="white"/>`);
    parts.push(
        `<rect x="${BORDER_ORIGIN_MM}" y="${BORDER_ORIGIN_MM}" width="${BORDER_PATH_SIZE_MM}" height="${BORDER_PATH_SIZE_MM}" fill="white" stroke="black" stroke-width="${BORDER_MM}"/>`
    );

    for (let row = 0; row < GRID_N; row++) {
        for (let col = 0; col < GRID_N; col++) {
            const idx = row * GRID_N + col;
            if (idx >= normalized.length) continue;
            const digit = normalized[idx];
            const [tileKey, angle] = STATE_MAP[digit];
            const tile = tiles[tileKey];
            const x = GRID_ORIGIN_MM + col * TILE_SIZE_MM;
            const y = GRID_ORIGIN_MM + row * TILE_SIZE_MM;
            const cx = tile.w / 2;
            const cy = tile.h / 2;
            const scale = TILE_SIZE_MM / tile.w;
            parts.push(
                `<g transform="translate(${x} ${y}) scale(${scale}) rotate(${angle} ${cx} ${cy})">${tile.body}</g>`
            );
        }
    }

    parts.push('</svg>');
    return parts.join('\n');
}
